import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSpriteMessage, hideSpriteMessage } from "../../store/sceneSlice";
import textToSpeechManager from "../../utils/TextToSpeechManager";

const CONFIDENCE_THRESHOLD = 0.6;
const STABLE_FRAMES_REQUIRED = 2;
const DEBOUNCE_MS = 2500;
const INFERENCE_INTERVAL_MS = 400;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeLabel = (label) => {
  if (!label) return label;
  return String(label).replace(/_/g, " ").trim();
};

const ObjectDetectionController = ({ selectedActorId }) => {
  const dispatch = useDispatch();
  const { scenes, currentSceneIndex, cardModeEnabled } = useSelector((s) => s.scene);

  const actorInfo = useMemo(() => {
    const scene = scenes?.[currentSceneIndex];
    const actors = scene?.actors || [];
    if (selectedActorId) {
      const target = actors.find((a) => a.id === selectedActorId) || actors[0] || null;
      return {
        id: target?.id || null,
        lang: target?.speechSettings?.lang || "en-US",
      };
    }
    const fallback = actors[0] || null;
    return {
      id: fallback?.id || null,
      lang: fallback?.speechSettings?.lang || "en-US",
    };
  }, [scenes, currentSceneIndex, selectedActorId]);
  const actorId = actorInfo?.id || null;
  const actorLang = actorInfo?.lang || "en-US";

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const modelRef = useRef(null);
  const loadingRef = useRef(false);
  const timerRef = useRef(null);
  const speakingRef = useRef(false);
  const detectionRef = useRef({
    candidateLabel: null,
    candidateCount: 0,
    lastTriggerLabel: null,
    lastTriggerAt: 0,
  });
  const lastDebugRef = useRef(0);
  const lastWaitLogRef = useRef(0);

  useEffect(() => {
    if (cardModeEnabled) {
      console.log("🟩 Object Detection enabled");
    } else {
      console.log("🟥 Object Detection disabled");
    }
  }, [cardModeEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (!cardModeEnabled) {
      setStatus("idle");
      setError(null);
      return undefined;
    }
    if (modelRef.current || loadingRef.current) {
      setStatus(modelRef.current ? "ready" : "loading");
      return undefined;
    }

    loadingRef.current = true;
    setStatus("loading");
    setError(null);

    (async () => {
      try {
        const tf = await import("@tensorflow/tfjs");
        await tf.ready();
        const cocoSsd = await import("@tensorflow-models/coco-ssd");
        if (cancelled) return;

        const model = await cocoSsd.load();
        if (cancelled) return;
        modelRef.current = model;
        setStatus("ready");
        console.log("✅ Object detection model loaded");
      } catch (err) {
        console.error("Object detection model load failed:", err);
        if (!cancelled) {
          setError(err);
          setStatus("error");
        }
      } finally {
        loadingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cardModeEnabled]);

  useEffect(() => {
    if (!cardModeEnabled) {
      return undefined;
    }
    if (status !== "ready" || !modelRef.current) {
      return undefined;
    }

    let active = true;

    const runInference = async () => {
      if (!active) return;

      const videoEl = window.humanDetectionData?.videoEl;
      const cameraActive = window.humanDetectionData?.cameraActive;
      if (!cameraActive || !videoEl || videoEl.readyState < 2) {
        const nowWait = Date.now();
        if (nowWait - lastWaitLogRef.current > 1500) {
          lastWaitLogRef.current = nowWait;
          console.log("⏳ Object detection waiting for camera...");
        }
        timerRef.current = setTimeout(runInference, INFERENCE_INTERVAL_MS);
        return;
      }

      const model = modelRef.current;

      try {
        const predictions = await model.detect(videoEl);
        let best = null;
        let bestScore = 0;
        for (const pred of predictions) {
          if (pred?.score > bestScore) {
            bestScore = pred.score;
            best = pred;
          }
        }

        const rawLabel = normalizeLabel(best?.class || null);
        const nowDebug = Date.now();
        if (nowDebug - lastDebugRef.current > 1000) {
          lastDebugRef.current = nowDebug;
          console.log("🟦 Object prediction:", rawLabel, bestScore.toFixed(3));
        }

        if (!rawLabel || bestScore < CONFIDENCE_THRESHOLD) {
          detectionRef.current.candidateLabel = null;
          detectionRef.current.candidateCount = 0;
          timerRef.current = setTimeout(runInference, INFERENCE_INTERVAL_MS);
          return;
        }

        if (rawLabel === detectionRef.current.candidateLabel) {
          detectionRef.current.candidateCount += 1;
        } else {
          detectionRef.current.candidateLabel = rawLabel;
          detectionRef.current.candidateCount = 1;
        }

        if (detectionRef.current.candidateCount < STABLE_FRAMES_REQUIRED) {
          timerRef.current = setTimeout(runInference, INFERENCE_INTERVAL_MS);
          return;
        }

        const now = Date.now();
        if (
          detectionRef.current.lastTriggerLabel === rawLabel &&
          now - detectionRef.current.lastTriggerAt < DEBOUNCE_MS
        ) {
          timerRef.current = setTimeout(runInference, INFERENCE_INTERVAL_MS);
          return;
        }

        if (!speakingRef.current) {
          speakingRef.current = true;
          detectionRef.current.lastTriggerLabel = rawLabel;
          detectionRef.current.lastTriggerAt = now;

          const displayText = rawLabel;
          const speechText = rawLabel;
          const lang = actorLang;

          if (actorId && displayText) {
            dispatch(showSpriteMessage({ actorId: actorId, message: displayText }));
          }

          try {
            const spoke = await textToSpeechManager.speak(speechText, lang);
            if (!spoke) {
              await delay(1500);
            }
          } catch (speechErr) {
            console.warn("Object speech failed:", speechErr);
          }

          if (actorId) {
            dispatch(hideSpriteMessage({ actorId: actorId }));
          }

          speakingRef.current = false;
        }
      } catch (err) {
        console.warn("Object detection inference error:", err);
      }

      timerRef.current = setTimeout(runInference, INFERENCE_INTERVAL_MS);
    };

    runInference();

    return () => {
      active = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      detectionRef.current.candidateLabel = null;
      detectionRef.current.candidateCount = 0;
    };
  }, [actorId, actorLang, cardModeEnabled, status]);

  if (status === "error") {
    console.warn("ObjectDetectionController error:", error);
  }

  return null;
};

export default ObjectDetectionController;
